import { inject } from '@angular/core'
import { tapResponse } from '@ngrx/operators'
import { signalStoreFeature, withMethods, withState } from '@ngrx/signals'
import { Events, injectDispatch, on, withReducer } from '@ngrx/signals/events'
import { rxMethod } from '@ngrx/signals/rxjs-interop'
import { Store } from '@ngrx/store'
import { concat, defer, EMPTY, exhaustMap, ignoreElements, pipe, takeUntil, tap } from 'rxjs'

import * as fromAccount from '@account/ngrx'
import { conversationEvents } from '@app/modules/messaging/components/store/conversation.events'
import { CONVERSATION_STORE_CONFIG } from '@app/modules/messaging/components/store/conversation.store'
import { injectDefaultErrorHandler } from '@app/modules/messaging/components/store/default-error-handler'
import type { Conversation } from '@app/modules/messaging/models/conversation.model'
import type { MessageForm } from '@app/modules/messaging/models/message-form.model'
import { ConversationUIService } from '@app/modules/messaging/services/conversation-ui.service'
import { MessagingService } from '@app/modules/messaging/services/messaging.service'
import { CreditsNavigationService } from '@app/modules/navigation/services/credits-navigation.service'
import type { S3BatchFileUpload } from '@app/modules/s3-upload/core/s3-batch-file-upload'

import { photoUploadEvents } from './photo-upload.events'
import type { MessagePhotoAlbumPhoto } from '../models/message-photo-album-photo.model'
import { MessagePhotoAlbumService } from '../services/message-photo-album.service'
import { UrlBlobCacheStore } from '@app/modules/upload/services/url-blob-cache.store'

interface UnsentMessagePhotosFeatureState {
  photoUploadInProgress: boolean
}

const initialState: UnsentMessagePhotosFeatureState = {
  photoUploadInProgress: false,
}

export const photoUploadFeature = () =>
  signalStoreFeature(
    withState(initialState),
    withMethods(
      (
        _store,
        conversationUIService = inject(ConversationUIService),
        messagePhotoAlbumService = inject(MessagePhotoAlbumService),
        urlBlobCacheStore = inject(UrlBlobCacheStore),
        dispatch = injectDispatch(photoUploadEvents),
        messagingService = inject(MessagingService),
        creditsNavigationService = inject(CreditsNavigationService),
        source = inject(CONVERSATION_STORE_CONFIG).source,
        globalStore = inject(Store),
        defaultErrorHandler = injectDefaultErrorHandler(),
        events = inject(Events),
      ) => {
        const handlePhotoUpload = rxMethod<{
          batchUpload: S3BatchFileUpload
          conversation: Conversation
          messageForm: MessageForm | undefined
        }>(
          pipe(
            exhaustMap(({ batchUpload, conversation, messageForm }) => {
              dispatch.uploadStart()

              const instance = conversationUIService.openUploadStatusComponent()
              instance.mediaType = 'Photo'
              instance.count = batchUpload.uploads.length

              let photos: MessagePhotoAlbumPhoto[] = []

              return concat(
                batchUpload.uploadChange$.pipe(
                  tap(() => instance.updateProgress(batchUpload.getTotalProgress())),
                  ignoreElements(),
                ),
                defer(() => {
                  const successful = batchUpload.successful
                  if (successful.length === 0) {
                    return EMPTY
                  }

                  return messagePhotoAlbumService
                    .uploadPhotos({
                      transloadit: batchUpload.assembly,
                      successful: successful.map(u => u.media),
                      profile_browse_id: conversation.profile.browse_id,
                    })
                    .pipe(
                      tapResponse({
                        next: response => {
                          urlBlobCacheStore.add(batchUpload.successful)
                          photos = response.media
                          globalStore.dispatch(
                            fromAccount.Actions.update({ partial: { has_message_album_photo: true } }),
                          )
                        },
                        error: defaultErrorHandler,
                      }),
                    )
                }),
              ).pipe(
                tap({
                  complete: () =>
                    dispatch.uploadComplete({
                      batchUpload,
                      photos,
                      profile: conversation.profile,
                      messageForm,
                    }),
                  finalize: () => {
                    instance.close()
                    dispatch.uploadEnd()
                  },
                }),
                takeUntil(events.on(conversationEvents.select)),
              )
            }),
          ),
        )
        const attemptToUploadPhotos = (
          batchUpload: S3BatchFileUpload,
          conversation: Conversation,
          messageForm: MessageForm | undefined,
        ) => {
          const affordablePhotosCount = messagingService.affordableMessagePhotosCount(conversation)

          if (affordablePhotosCount < batchUpload.uploads.length) {
            const instance = conversationUIService.openMessagePhotoAlbumInsufficientCreditsModal()
            instance.affordablePhotosCount = affordablePhotosCount
            instance.mode = 'upload'
            instance.onConfirm(action => {
              if (action === 'Buy Credits') {
                creditsNavigationService.visitCredits('sendMessagePhoto', {
                  source,
                  profile_id: conversation.profile.id,
                })
              } else {
                handlePhotoUpload({
                  batchUpload: batchUpload.copy(affordablePhotosCount),
                  conversation,
                  messageForm,
                })
              }
            })

            return
          }

          handlePhotoUpload({ batchUpload, conversation, messageForm })
        }

        return {
          attemptToUploadPhotos,
          handlePhotoUpload,
        }
      },
    ),
    withReducer(
      on(photoUploadEvents.uploadStart, () => ({ photoUploadInProgress: true })),
      on(photoUploadEvents.uploadEnd, () => ({ photoUploadInProgress: false })),
    ),
  )
